import { calcCapitalGainsTaxLiability, calcFutureValue, calcFutureValueOfMonthlyContributions } from '@shared';

export function calcNetFutureValueForAccount(
  detail: LtcFinancialPlanningAccountDetail,
  yearsTillLtc: number,
  contributionMonthCount: number,
): number {
  const annualRateOfReturn = detail.expectedReturn / 100;
  const gainsTaxRate = detail.capitalGainsTaxRate / 100;
  const monthlyContributionAmount = detail.futureContributions;

  const futureValueOfInitialInvestment = calcFutureValue(
    detail.currentValue,
    annualRateOfReturn,
    yearsTillLtc,
  );

  const taxOnInitialInvestment = calcCapitalGainsTaxLiability(
    detail.currentValue,
    gainsTaxRate,
    futureValueOfInitialInvestment,
  );

  const netFutureValueOfInitialInvestment =
    futureValueOfInitialInvestment - taxOnInitialInvestment;

  const futureValueOfContributions = calcFutureValueOfMonthlyContributions(
    monthlyContributionAmount,
    annualRateOfReturn,
    contributionMonthCount,
  );

  const totalContributed = monthlyContributionAmount * contributionMonthCount;
  const taxOnContributions = calcCapitalGainsTaxLiability(
    totalContributed,
    gainsTaxRate,
    futureValueOfContributions,
  );

  const netFutureValueOfContributions =
    futureValueOfContributions - taxOnContributions;

  return netFutureValueOfInitialInvestment + netFutureValueOfContributions;
}

export function calculateTotalContribution(
  allocation: number,
  fundingMode: LtcFinancialPlanningFundingMode,
  paymentOption: LtcFinancialPlanningPaymentOption,
  contributionMonthCount: number,
): number {
  if (fundingMode === 'lumpSum') {
    return allocation;
  }

  // Monthly mode - calculate total based on payment type
  switch (paymentOption) {
    case '10-Pay':
      return allocation * 10 * 12; // 120 months
    case 'Monthly':
    case 'Annually':
      return allocation * contributionMonthCount;
    default:
      return allocation;
  }
}

export function calcSelfFundingCapacity(
  selectedAssets: LtcFinancialPlanningAssetType[],
  accountDetails: Partial<Record<LtcFinancialPlanningAssetType, LtcFinancialPlanningAccountDetail>>,
  yearsTillLtc: number,
  contributionMonthCount: number,
): number {
  return selectedAssets.reduce((sum, assetType) => {
    const detail = accountDetails[assetType];
    if (!detail) return sum;
    return sum + calcNetFutureValueForAccount(detail, yearsTillLtc, contributionMonthCount);
  }, 0);
}

export function calcAdjustedSelfFunding(
  selectedAssets: LtcFinancialPlanningAssetType[],
  accountDetails: Partial<Record<LtcFinancialPlanningAssetType, LtcFinancialPlanningAccountDetail>>,
  fundingAllocations: Partial<Record<LtcFinancialPlanningAssetType, number>>,
  yearsTillLtc: number,
  contributionMonthCount: number,
): number {
  return selectedAssets.reduce((sum, assetType) => {
    const detail = accountDetails[assetType];
    if (!detail) return sum;

    const allocation = fundingAllocations[assetType] || 0;
    if (allocation > 0) {
      // Create adjusted detail with reduced current value
      const adjustedDetail: LtcFinancialPlanningAccountDetail = {
        ...detail,
        currentValue: Math.max(0, detail.currentValue - allocation),
      };
      return (
        sum + calcNetFutureValueForAccount(adjustedDetail, yearsTillLtc, contributionMonthCount)
      );
    }
    return sum + calcNetFutureValueForAccount(detail, yearsTillLtc, contributionMonthCount);
  }, 0);
}

export function calcTotalPolicyCost(
  fundingAllocations: Partial<Record<LtcFinancialPlanningAssetType, number>>,
): number {
  return Object.values(fundingAllocations).reduce(
    (sum: number, amt) => sum + (typeof amt === 'number' ? amt : 0),
    0,
  );
}

export function buildComparisonScenarios(
  selfFundingCapacity: number,
  adjustedSelfFunding: number,
  policyCoverage: number,
  projectedLtcCost: number,
): {
  withoutInsurance: {
    remainingAssets: number;
    totalCoverage: number;
    coveragePercent: number;
    surplus: number;
    gap: number;
  };
  withInsurance: {
    remainingAssets: number;
    policyCoverage: number;
    totalCoverage: number;
    coveragePercent: number;
    surplus: number;
    gap: number;
  };
} {
  // Remaining LTC cost after insurance pays its portion
  const remainingLtcCostAfterInsurance = Math.max(0, projectedLtcCost - policyCoverage);

  return {
    withoutInsurance: {
      remainingAssets: selfFundingCapacity - projectedLtcCost,
      totalCoverage: selfFundingCapacity,
      coveragePercent: Math.round((selfFundingCapacity / projectedLtcCost) * 100),
      surplus: Math.max(0, selfFundingCapacity - projectedLtcCost),
      gap: Math.max(0, projectedLtcCost - selfFundingCapacity),
    },
    withInsurance: {
      // Remaining assets = adjusted growth (from reduced principal) - remaining LTC cost (after insurance pays)
      remainingAssets: adjustedSelfFunding - remainingLtcCostAfterInsurance,
      policyCoverage: policyCoverage,
      totalCoverage: adjustedSelfFunding + policyCoverage,
      coveragePercent: Math.round(
        ((adjustedSelfFunding + policyCoverage) / projectedLtcCost) * 100,
      ),
      surplus: Math.max(0, adjustedSelfFunding + policyCoverage - projectedLtcCost),
      gap: Math.max(0, projectedLtcCost - (adjustedSelfFunding + policyCoverage)),
    },
  };
}

export function calcFundingBreakdown(
  fundingAllocations: Partial<Record<LtcFinancialPlanningAssetType, number>>,
  accountDetails: Partial<Record<LtcFinancialPlanningAssetType, LtcFinancialPlanningAccountDetail>>,
  yearsTillLtc: number,
): Array<{
  assetType: LtcFinancialPlanningAssetType;
  amountIn: number;
  projectedValue: number;
  roi: number;
}> {
  return Object.entries(fundingAllocations)
    .filter(([_, amount]) => typeof amount === 'number' && amount > 0)
    .map(([assetType, amountIn]) => {
      const detail = accountDetails[assetType as LtcFinancialPlanningAssetType];
      const expectedReturn = (detail?.expectedReturn || 7) / 100;
      const gainsTaxRate = (detail?.capitalGainsTaxRate || 15) / 100;

      const grossProjectedValue = calcFutureValue(amountIn, expectedReturn, yearsTillLtc);
      const taxOnAllocation = calcCapitalGainsTaxLiability(
        amountIn,
        gainsTaxRate,
        grossProjectedValue,
      );
      const projectedValue = grossProjectedValue - taxOnAllocation;

      const roi = Math.round((projectedValue / amountIn - 1) * 100);

      return {
        assetType: assetType as LtcFinancialPlanningAssetType,
        amountIn: amountIn,
        projectedValue,
        roi,
      };
    });
}

export function calcProtectedAssets(
  priorityAssets: LtcFinancialPlanningAssetType[],
  accountDetails: Partial<Record<LtcFinancialPlanningAssetType, LtcFinancialPlanningAccountDetail>>,
  yearsTillLtc: number,
  contributionMonthCount: number,
): Array<{
  assetType: LtcFinancialPlanningAssetType;
  amountUsed: number;
  futureValue: number;
}> {
  return priorityAssets.map(assetType => {
    const detail = accountDetails[assetType];
    if (!detail) {
      return { assetType, amountUsed: 0, futureValue: 0 };
    }
    const futureValue = calcNetFutureValueForAccount(detail, yearsTillLtc, contributionMonthCount);
    return {
      assetType,
      amountUsed: 0,
      futureValue,
    };
  });
}
